import { PhoneIcon } from '@/assets/icons/completion-step/PhoneIcon';
import IconHorizontalRebrand from '@/assets/logos/icon-horizontal-svg/icon-horizontal-rebrand.svg';
import IconRoundBarkBgWhite from '@/assets/logos/icon-round-bark-bg-svg/icon-round-bark-bg-white.svg';
import { ForestPatternBackground } from '@/features/onboarding-v2/components/ForestPatternBackground';
import { SunlightPatternBackground } from '@/features/onboarding-v2/components/SunlightPatternBackground';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import Image from 'next/image';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { ChoiceCard } from './choice-card';

interface ChoiceStepProps {
  onSignupClick: () => void;
  onBookDemoClick: () => void;
}

export function ChoiceStep({ onSignupClick, onBookDemoClick }: ChoiceStepProps) {
  const intl = useIntl();

  const hasTrackedScreenView = useRef(false);
  useEffect(() => {
    if (hasTrackedScreenView.current) return;
    hasTrackedScreenView.current = true;
    trackScreenViewed({ screen_name: ScreenName.SlgFlow });
  }, []);

  const trackButton = useCallback((action: ButtonAction, label: string) => {
    trackButtonClicked({
      screen_name: ScreenName.SlgFlow,
      action,
      label,
    });
  }, []);

  const handleSignupClick = useCallback(() => {
    trackButton(ButtonAction.SlgChoiceSignup, 'Sign up free');
    onSignupClick();
  }, [trackButton, onSignupClick]);

  const handleBookDemoClick = useCallback(() => {
    trackButton(ButtonAction.SlgChoiceBookDemo, 'Book a Demo');
    onBookDemoClick();
  }, [trackButton, onBookDemoClick]);

  return (
    <div className="relative z-10 flex w-full flex-col items-center gap-10 px-6 py-8">
      {/* Header */}
      <div className="flex flex-col items-center gap-6">
        <Image
          src={IconHorizontalRebrand}
          alt="Heidi"
          width={80}
          height={24}
          className="dark-invert"
        />
        <div className="flex flex-col items-center gap-1 text-center">
          <TypographyH2>
            <FormattedMessage id="4LMw06FeSc" defaultMessage="Use Heidi, save hours" />
          </TypographyH2>
          <TypographyP2 className="text-text-secondary">
            <FormattedMessage id="KHi8Q0VbN2" defaultMessage="Let's get you started" />
          </TypographyP2>
        </div>
      </div>

      {/* Choice cards */}
      <div className="flex w-full max-w-[500px] flex-col gap-4 rounded-2xl bg-surface p-6 shadow-lg">
        <ChoiceCard
          title={intl.formatMessage({
            id: 'cPh9OCFhsM',
            defaultMessage: 'Sign up free',
          })}
          description={intl.formatMessage({
            id: 'TpStGaZtU0',
            defaultMessage: 'Get started in seconds',
          })}
          data-testid="choice-signup"
          icon={
            <div className="relative flex size-full items-center justify-center">
              <SunlightPatternBackground />
              <div className="relative z-10 rounded-full border border-bark-900/20 p-1.5">
                <div className="rounded-full border border-bark-900/30 p-1">
                  <Image src={IconRoundBarkBgWhite} alt="" width={44} height={44} />
                </div>
              </div>
            </div>
          }
          onClick={handleSignupClick}
        />
        <ChoiceCard
          title={intl.formatMessage({
            id: '0JV7KO00X3',
            defaultMessage: 'Book a Demo',
          })}
          description={intl.formatMessage({
            id: 'hijdJW2S1I',
            defaultMessage: 'Speak to our team',
          })}
          data-testid="choice-book-demo"
          icon={
            <div className="relative flex size-full items-center justify-center">
              <ForestPatternBackground />
              <div className="relative z-10 rounded-full border border-bark-900/20 p-1.5">
                <div className="rounded-full border border-bark-900/30 p-1">
                  <div className="flex size-11 items-center justify-center rounded-full bg-background-primary">
                    <PhoneIcon className="size-6 text-text-primary-on-dark" />
                  </div>
                </div>
              </div>
            </div>
          }
          onClick={handleBookDemoClick}
        />
      </div>
    </div>
  );
}
